import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { gsap } from 'gsap';
import TKFIcon from '../icons/TKFIcon';

// Main header container with transparent background
const HeaderContainer = styled.header<{ $isScrolled: boolean; $isHomePage: boolean }>`
  background: transparent;
  color: ${props => props.theme.colors.ink};
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all ${props => props.theme.animation.duration.slow} ${props => props.theme.animation.easing.easeInOut};
`;

const Nav = styled.nav`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    justify-content: space-between;
  }
`;

const Logo = styled(Link)`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  color: ${props => props.theme.colors.ink};
  transition: color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};

  &:hover {
    color: ${props => props.theme.colors.primary[700]};
  }

  /* Desktop */
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
  }

  /* Mobile */
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }
`;

const LogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -${props => props.theme.spacing[1]};

  svg {
    width: 40px;
    height: 40px;
  }
`;

const RectangularNavbar = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-radius: ${props => props.theme.borderRadius.sharp};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  width: 100%;
`;

const NavbarContent = styled.div`
  max-width: ${props => props.theme.layout.maxWidth};
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[5]};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[8]};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[10]};
  }
`;

/**
 * Keeps the nav optically centred: an empty element the same width as the
 * wordmark balances it on the right, as in the reference layout.
 */
const NavSeparator = styled.div`
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    width: 96px;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing[8]};
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    position: absolute;
    top: calc(100% + ${props => props.theme.spacing[3]});
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 999;
    border-radius: ${props => props.theme.borderRadius.lg};
    box-shadow: ${props => props.theme.shadows.lg};
    border: 1px solid ${props => props.theme.colors.border};
    padding: ${props => props.theme.spacing[5]} ${props => props.theme.spacing[4]};
    gap: ${props => props.theme.spacing[2]};

    /* Initial hidden state - GSAP will animate */
    opacity: ${props => props.isOpen ? 1 : 0};
    transform: translateY(${props => props.isOpen ? '0px' : '-20px'}) scale(${props => props.isOpen ? 1 : 0.95});
    pointer-events: ${props => props.isOpen ? 'auto' : 'none'};

    /* Responsive adjustments */
    margin: 0 ${props => props.theme.spacing[2]};

    /* Small mobile phones */
    @media (max-width: 480px) {
      margin: 0 ${props => props.theme.spacing[1]};
      padding: ${props => props.theme.spacing[5]} ${props => props.theme.spacing[3]};
      gap: ${props => props.theme.spacing[3]};
    }
  }
`;

const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, ${props => props.$isOpen ? '0.1' : '0'});
  z-index: 998;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(${props => props.$isOpen ? '2px' : '0px'});
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ isActive?: boolean }>`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-size: ${props => props.theme.typography.fontSize.sm};
  letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
  text-decoration: none;
  position: relative;
  transition: color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    color: ${props => props.isActive ? props.theme.colors.ink : props.theme.colors.textMuted};
    padding: ${props => props.theme.spacing[2]} 0;

    /* Active pages are marked with a single green dot, nothing heavier. */
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      width: 4px;
      height: 4px;
      border-radius: ${props => props.theme.borderRadius.full};
      background: ${props => props.theme.colors.primary[500]};
      transform: translateX(-50%) scale(${props => props.isActive ? 1 : 0});
      transition: transform ${props => props.theme.animation.duration.normal} ${props => props.theme.animation.easing.easeOut};
    }

    &:hover {
      color: ${props => props.theme.colors.ink};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    color: ${props => props.isActive ? props.theme.colors.ink : props.theme.colors.textMuted};
    font-size: ${props => props.theme.typography.fontSize.base};
    padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
    /* Initial state - GSAP will animate from here */
    opacity: 0;
    transform: translateY(-10px);
    border: none;
    text-align: center;
    background: ${props => props.isActive ? props.theme.colors.neutral[100] : 'transparent'};
    border-radius: ${props => props.theme.borderRadius.minimal};
    width: 100%;
    max-width: 220px;
    transition: color 0.2s ease, background-color 0.2s ease;

    &:hover {
      color: ${props => props.theme.colors.ink};
      background: ${props => props.theme.colors.neutral[100]};
    }

    /* Small mobile adjustments */
    @media (max-width: 480px) {
      font-size: ${props => props.theme.typography.fontSize.base};
      padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
      max-width: 180px;
    }

    /* Very small screens */
    @media (max-width: 360px) {
      font-size: ${props => props.theme.typography.fontSize.sm};
      padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
      max-width: 160px;
    }
  }
`;

/**
 * Labelled pill rather than a bare hamburger — the label makes the affordance
 * obvious and the icon sits in its own green chip, echoing the buttons.
 */
const MenuButton = styled(motion.button)<{ $isOpen: boolean; $isAnimating: boolean }>`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.ink};
  border: none;
  cursor: ${props => props.$isAnimating ? 'wait' : 'pointer'};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[2]} ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.minimal};
  min-height: 40px;
  transition: background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;
  opacity: ${props => props.$isAnimating ? 0.7 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary[700]};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }

  /* Desktop - hide menu button */
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }

  /* Small mobile adjustments */
  @media (max-width: 480px) {
    padding-left: ${props => props.theme.spacing[3]};
    min-height: 36px;
  }

  .menu-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: ${props => props.theme.borderRadius.sm};
    background: ${props => props.theme.colors.primary[500]};
    flex-shrink: 0;
  }

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
  }
`;


const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navLinksRef = useRef<HTMLDivElement>(null);
  const navLinkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const isActive = (path: string) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  // Function to clear GSAP inline styles for desktop
  const clearMobileStyles = () => {
    if (navLinksRef.current && navLinkRefs.current) {
      gsap.set(navLinksRef.current, { clearProps: "all" });
      gsap.set(navLinkRefs.current, { clearProps: "all" });
    }
  };

  const toggleMenu = useCallback(() => {
    if (isAnimating || !isMobile) return; // Prevent multiple rapid toggles and only work on mobile

    setIsAnimating(true);
    const newIsOpen = !isMenuOpen;

    // Kill any existing timeline
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    // Create new timeline
    timelineRef.current = gsap.timeline({
      onComplete: () => {
        setIsAnimating(false);
        timelineRef.current = null;
      }
    });

    if (newIsOpen) {
      // Opening dropdown menu
      setIsMenuOpen(true);

      // Set initial states for dropdown
      gsap.set(navLinksRef.current, {
        opacity: 0,
        y: -20,
        scale: 0.95,
        transformOrigin: 'top center'
      });
      gsap.set(navLinkRefs.current, {
        opacity: 0,
        y: -10
      });

      // Animate dropdown in
      timelineRef.current
        .to(navLinksRef.current, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.3,
          ease: 'back.out(1.7)'
        })
        .to(navLinkRefs.current, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.1,
          ease: 'power2.out'
        }, '-=0.15');

    } else {
      // Closing dropdown menu
      timelineRef.current
        .to(navLinkRefs.current, {
          opacity: 0,
          y: -10,
          duration: 0.2,
          stagger: 0.05,
          ease: 'power2.in'
        })
        .to(navLinksRef.current, {
          opacity: 0,
          y: -20,
          scale: 0.95,
          duration: 0.25,
          ease: 'power2.in',
          onComplete: () => setIsMenuOpen(false)
        }, '-=0.1');
    }
  }, [isAnimating, isMobile, isMenuOpen]);

  // Handle responsive breakpoint changes and clear GSAP styles
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768; // md breakpoint
      
      if (isMobile && !newIsMobile) {
        // Switching from mobile to desktop - clear mobile styles
        if (timelineRef.current) {
          timelineRef.current.kill();
          timelineRef.current = null;
        }
        setIsMenuOpen(false);
        setIsAnimating(false);
        clearMobileStyles();
      }
      
      setIsMobile(newIsMobile);
    };

    // Set initial mobile state
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Handle click outside, escape key, and scroll
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      const scrollThreshold = heroHeight * 0.5;
      const scrolled = window.scrollY > scrollThreshold;
      setIsScrolled(scrolled);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && !isAnimating && navLinksRef.current && !navLinksRef.current.contains(event.target as Node)) {
        const menuButton = document.querySelector('[data-menu-button]');
        if (!menuButton?.contains(event.target as Node)) {
          toggleMenu();
        }
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen && !isAnimating) {
        toggleMenu();
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMenuOpen, isAnimating, toggleMenu]);

  // Initialize and cleanup GSAP
  useEffect(() => {
    // Clear any existing inline styles on mount
    clearMobileStyles();
    
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      clearMobileStyles();
    };
  }, []);

  return (
    <>
      <MobileOverlay $isOpen={isMenuOpen} onClick={() => !isAnimating && toggleMenu()} />
      <HeaderContainer $isScrolled={isScrolled} $isHomePage={isHomePage}>
        <Nav>
        <RectangularNavbar>
          <NavbarContent>
            <Logo to="/">
              <LogoIcon>
                <TKFIcon size={48} />
              </LogoIcon>
              TKF
            </Logo>

            <NavLinks ref={navLinksRef} isOpen={isMenuOpen}>
              <NavLink
                ref={(el) => { navLinkRefs.current[0] = el; }}
                to="/"
                isActive={isActive('/')}
                onClick={() => isMenuOpen && !isAnimating && toggleMenu()}
              >
                Home
              </NavLink>
              <NavLink
                ref={(el) => { navLinkRefs.current[1] = el; }}
                to="/about-us"
                isActive={isActive('/about-us')}
                onClick={() => isMenuOpen && !isAnimating && toggleMenu()}
              >
                About Us
              </NavLink>
              <NavLink
                ref={(el) => { navLinkRefs.current[2] = el; }}
                to="/initiatives"
                isActive={isActive('/initiatives')}
                onClick={() => isMenuOpen && !isAnimating && toggleMenu()}
              >
                Initiatives
              </NavLink>
              <NavLink
                ref={(el) => { navLinkRefs.current[3] = el; }}
                to="/contact"
                isActive={isActive('/contact')}
                onClick={() => isMenuOpen && !isAnimating && toggleMenu()}
              >
                Contact
              </NavLink>
            </NavLinks>

            <NavSeparator aria-hidden="true" />

            <MenuButton
              $isOpen={isMenuOpen}
              $isAnimating={isAnimating}
              disabled={isAnimating}
              onClick={toggleMenu}
              whileTap={{ scale: 0.97 }}
              data-menu-button
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? 'Close' : 'Menu'}
              <span className="menu-icon" aria-hidden="true">
                {isMenuOpen ? <X size={14} /> : <Menu size={14} />}
              </span>
            </MenuButton>
          </NavbarContent>
        </RectangularNavbar>
      </Nav>
    </HeaderContainer>
    </>
  );
};

export default Header;